import { Alert, Button } from 'antd';
import { useEffect, useRef, useState } from 'react';

type VersionJsonResponse = Readonly<{
  buildVersion: string;
  commitSha?: string;
  builtAtIso?: string;
}>;

type ServiceWorkerSkipWaitingMessage = Readonly<{ type: 'SKIP_WAITING' }>;

function isVersionJsonResponse(value: unknown): value is VersionJsonResponse {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.buildVersion !== 'string') {
    return false;
  }

  if (record.commitSha !== undefined && typeof record.commitSha !== 'string') {
    return false;
  }

  if (record.builtAtIso !== undefined && typeof record.builtAtIso !== 'string') {
    return false;
  }

  return true;
}

async function fetchLatestVersion(signal: AbortSignal): Promise<VersionJsonResponse | null> {
  const response = await fetch('/version.json', {
    method: 'GET',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    return null;
  }

  const json: unknown = await response.json();
  return isVersionJsonResponse(json) ? json : null;
}

async function tryActivateLatestServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) {
    return;
  }

  try {
    await registration.update();
  } catch {
    // ignore
  }

  const waiting = registration.waiting;
  if (waiting) {
    const message: ServiceWorkerSkipWaitingMessage = { type: 'SKIP_WAITING' };
    waiting.postMessage(message);
  }

  await new Promise<void>((resolve) => {
    const timeoutId = window.setTimeout(resolve, 2000);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        window.clearTimeout(timeoutId);
        resolve();
      },
      { once: true }
    );
  });
}

function reloadWithCacheBuster(): void {
  const url = new URL(window.location.href);
  url.searchParams.set('__reload', String(Date.now()));
  window.location.replace(url.toString());
}

async function forceAppReload(): Promise<void> {
  await tryActivateLatestServiceWorker();
  reloadWithCacheBuster();
}

export const BuildVersionUpdateNotification = () => {
  const currentBuildVersion = import.meta.env.VITE_BUILD_VERSION?.trim() ?? '';
  const shouldCheck = currentBuildVersion.length > 0;

  const [isOutdated, setIsOutdated] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const checkingInFlightRef = useRef(false);

  useEffect(() => {
    if (!shouldCheck) {
      return;
    }

    if (dismissed) {
      return;
    }

    const controller = new AbortController();

    const markOutdatedIfNeeded = (latest: VersionJsonResponse | null) => {
      if (!latest) {
        return;
      }
      if (latest.buildVersion && latest.buildVersion !== currentBuildVersion) {
        setIsOutdated(true);
      }
    };

    const shouldSkipCheck = () => checkingInFlightRef.current || dismissed;

    const checkOnce = async () => {
      if (shouldSkipCheck()) {
        return;
      }

      checkingInFlightRef.current = true;
      try {
        const latest = await fetchLatestVersion(controller.signal);
        markOutdatedIfNeeded(latest);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
      } finally {
        checkingInFlightRef.current = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkOnce();
      }
    };

    void checkOnce();
    document.addEventListener('visibilitychange', onVisibilityChange);
    const intervalId = window.setInterval(() => void checkOnce(), 60_000);

    return () => {
      controller.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [dismissed, shouldCheck]);

  if (!shouldCheck || dismissed || !isOutdated) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        padding: 8,
      }}
    >
      <Alert
        type="warning"
        showIcon
        closable
        onClose={() => setDismissed(true)}
        title="Update available"
        description="A newer version of the app is available. Reload the page to get the latest updates."
        action={
          <Button
            type="primary"
            loading={isReloading}
            onClick={() => {
              setIsReloading(true);
              void forceAppReload();
            }}
          >
            Reload
          </Button>
        }
      />
    </div>
  );
};
